import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug, Trash2, MessageSquare } from 'lucide-react';
import { captureException, addBreadcrumb, showUserFeedback } from '../config/sentry.config';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the component tree,
 * logs those errors, and displays a fallback UI instead of crashing the app
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID for debugging
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    // Collect comprehensive error context
    const errorContext = {
      errorId,
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      memory: (() => {
        try {
          return {
            used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
            total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
            limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
          };
        } catch {
          return 'N/A';
        }
      })(),
      localStorage: (() => {
        try {
          return Object.keys(localStorage).length;
        } catch {
          return 'N/A';
        }
      })(),
      sessionStorage: (() => {
        try {
          return Object.keys(sessionStorage).length;
        } catch {
          return 'N/A';
        }
      })()
    };

    // Log comprehensive error details
    console.error('[ErrorBoundary] Application Error:', errorContext);

    // Report to Sentry with enhanced context
    try {
      captureException(error, {
        tags: {
          errorId,
          errorBoundary: 'custom',
          recoveryAttempted: 'false',
        },
        extra: {
          componentStack: errorInfo.componentStack,
          errorContext,
        },
        level: 'error',
      });

      // Add breadcrumb for error boundary catch
      addBreadcrumb({
        category: 'error-boundary',
        message: 'Error caught by ErrorBoundary',
        level: 'error',
        data: {
          errorId,
          errorMessage: error.message,
          url: window.location.href,
        },
      });
    } catch (sentryError) {
      console.error('[ErrorBoundary] Failed to report to Sentry:', sentryError);
    }

    this.setState({
      error,
      errorInfo,
      errorId
    });

    // Report to Google Analytics (if available)
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.message,
        fatal: true
      });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleSendFeedback = () => {
    showUserFeedback();
  };

  handleClearCacheAndReload = () => {
    // Comprehensive cache and storage cleanup
    try {
      // Clear storage but PRESERVE auth tokens so recovery doesn't silently log the
      // user out (localStorage.clear() would wipe token/refreshToken/user).
      const AUTH_KEYS = ['token', 'refreshToken', 'user'];
      const preservedAuth = {};
      AUTH_KEYS.forEach(k => {
        const v = localStorage.getItem(k);
        if (v !== null) preservedAuth[k] = v;
      });
      localStorage.clear();
      Object.entries(preservedAuth).forEach(([k, v]) => localStorage.setItem(k, v));
      sessionStorage.clear();

      // Clear cache storage
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }

      // Unregister service workers
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister();
          });
        });
      }

      // Clear any indexedDB databases (if any)
      if ('indexedDB' in window) {
        indexedDB.databases().then(databases => {
          databases.forEach(db => {
            indexedDB.deleteDatabase(db.name);
          });
        });
      }

      console.log('[ErrorBoundary] Cache and storage cleared successfully');
    } catch (clearError) {
      console.warn('[ErrorBoundary] Error clearing cache:', clearError);
    }

    // Reload after a short delay to allow cleanup
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  handleReload = () => {
    window.location.reload();
  };

  handleOpenIncognito = () => {
    // Try to open in incognito/private mode
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isFirefox = /Firefox/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

    let incognitoUrl = '';

    if (isChrome) {
      incognitoUrl = 'chrome://settings/clearBrowserData';
    } else if (isFirefox) {
      incognitoUrl = 'about:preferences#privacy';
    } else if (isSafari) {
      incognitoUrl = 'about:blank';
    }

    if (incognitoUrl) {
      window.open(incognitoUrl, '_blank');
    }

    // Also provide instructions
    alert('To open in incognito mode:\n\nChrome: Ctrl+Shift+N\nFirefox: Ctrl+Shift+P\nSafari: File > New Private Window');
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Application Error
              </h1>
              <p className="text-gray-600">
                The application encountered an unexpected error. This is likely due to browser cache conflicts or memory issues.
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-red-800 mb-2">
                🚨 Most Likely Causes:
              </h3>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• <strong>Browser Cache Corruption:</strong> Cached data conflicts causing crashes</li>
                <li>• <strong>Memory Issues:</strong> Browser running low on memory</li>
                <li>• <strong>Storage Conflicts:</strong> localStorage/sessionStorage corruption</li>
                <li>• <strong>Extension Interference:</strong> Browser extensions blocking functionality</li>
                <li>• <strong>Service Worker Issues:</strong> Cached service workers causing problems</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-green-800 mb-3">
                💬 Help Us Fix This:
              </h3>
              <button
                onClick={this.handleSendFeedback}
                className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg transition-colors shadow-lg"
              >
                <MessageSquare className="h-4 w-4" />
                Send Feedback to Support Team
              </button>
              <p className="text-xs text-green-600 mt-2 text-center">
                Tell us what happened and we'll get this fixed quickly
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-blue-800 mb-3">
                🔧 Recovery Options:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={this.handleClearCacheAndReload}
                  className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Clear Cache & Reload
                </button>

                <button
                  onClick={this.handleOpenIncognito}
                  className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg transition-colors"
                >
                  🔒 Open Incognito
                </button>

                <button
                  onClick={this.handleReload}
                  className="flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Quick Reload
                </button>

                <button
                  onClick={() => window.open(window.location.href, '_blank')}
                  className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg transition-colors"
                >
                  <Home className="h-4 w-4" />
                  New Tab
                </button>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-yellow-800 mb-2">
                💡 Prevention Tips:
              </h3>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Use incognito mode for development/testing</li>
                <li>• Clear browser cache regularly (Ctrl+Shift+Delete)</li>
                <li>• Disable browser extensions temporarily</li>
                <li>• Restart browser if memory usage is high</li>
                <li>• Use different browsers for testing</li>
              </ul>
            </div>

            {this.state.errorId && (
              <div className="bg-gray-100 rounded p-3 mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Error ID:</strong> {this.state.errorId}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Share this ID with support for faster resolution.
                </p>
              </div>
            )}

            {/* Technical Details (Collapsible) */}
            <details className="mt-4">
              <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700 flex items-center">
                <Bug className="w-4 h-4 mr-1" />
                Technical Details
              </summary>
              <div className="mt-3 p-3 bg-gray-100 rounded text-xs font-mono">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div><strong>Browser:</strong> {(() => {
                    const ua = navigator.userAgent;
                    if (ua.includes('Chrome')) return 'Chrome';
                    if (ua.includes('Firefox')) return 'Firefox';
                    if (ua.includes('Safari')) return 'Safari';
                    if (ua.includes('Edge')) return 'Edge';
                    return 'Unknown';
                  })()}</div>
                  <div><strong>Platform:</strong> {navigator.platform}</div>
                  <div><strong>Online:</strong> {navigator.onLine ? 'Yes' : 'No'}</div>
                  <div><strong>Memory:</strong> {(() => {
                    try {
                      return Math.round(performance.memory.usedJSHeapSize / 1024 / 1024) + 'MB';
                    } catch {
                      return 'N/A';
                    }
                  })()}</div>
                </div>
                <div className="mb-2">
                  <strong>URL:</strong> {window.location.href}
                </div>
                <div className="mb-2">
                  <strong>Timestamp:</strong> {new Date().toISOString()}
                </div>
                {this.state.error?.message && (
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.message}
                  </div>
                )}
              </div>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
